<template>
  <div class="flex flex-col h-full mt-4">
    <!-- Enhanced Header Bar -->
    <div class="bg-white p-4 mb-4 rounded-lg shadow">
      <div class="flex flex-col gap-4">
        <!-- Top row with scan and repair button -->
        <div class="flex justify-between items-center">
          <div class="text-xl font-semibold text-gray-800">
            Orders
          </div>
          <div class="flex items-center gap-3">
            <div class="w-48">
              <InputText
                id="return"
                v-model="returnValue"
                placeholder="Scan Order..."
                type="text"
                class="w-full"
              />
            </div>
            <Button
              label="Add Order"
              icon="pi pi-plus"
              severity="secondary"
              raised
              @click="openAddDialog"
            />
          </div>
        </div>

        <!-- View type selector -->
        <div class="flex gap-4 items-center">
          <ButtonGroup>
            <Button
              label="By Order"
              :class="{ 'bg-symphony-yellow-500 text-black': viewType === 'order' }"
              class="flex-1 md:flex-none"
              severity="secondary"
              raised
              @click="viewType = 'order'"
            />
            <Button
              label="By Status"
              :class="{ 'bg-symphony-yellow-500 text-black': viewType === 'status' }"
              class="flex-1 md:flex-none"
              severity="secondary"
              raised
              @click="viewType = 'status'"
            />
          </ButtonGroup>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <div class="flex-1 bg-white rounded-lg shadow overflow-hidden">
      <DataTable
        v-if="viewType == 'order'"
        :value="orders"
        sort-field="orderNumber"
        scrollable
        scroll-height="550px"
        class="h-full"
        table-style="width: 100%"
        striped-rows
      >
        <template #empty>
          <div class="flex flex-wrap items-center justify-center p-4">
            <p>No orders have been added.</p>
          </div>
        </template>

        <Column
          field="orderNumber"
          header="Order Number"
          sortable
          :style="{ width: '20%', minWidth: '150px' }"
        />
        <Column
          field="supplier"
          header="Supplier"
          sortable
          :style="{ width: '30%', minWidth: '250px' }"
        />
        <Column
          field="status"
          header="Status"
          sortable
          :style="{ width: '20%', minWidth: '150px' }"
        />
        <Column
          field="total"
          header="Total"
          sortable
          :style="{ width: '10%', minWidth: '100px' }"
        />
        <Column
          field="date"
          header="Date"
          sortable
          :style="{ width: '20%', minWidth: '150px' }"
        />
      </DataTable>

      <DataTable
        v-else
        :value="orders"
        sort-field="status"
        scrollable
        scroll-height="550px"
        class="h-full"
        table-style="width: 100%"
        striped-rows
      >
        <template #empty>
          <div class="flex flex-wrap items-center justify-center p-4">
            <p>No orders have been added.</p>
          </div>
        </template>

        <Column
          field="status"
          header="Status"
          sortable
          :style="{ width: '30%', minWidth: '200px' }"
        />
        <Column
          field="orderNumber"
          header="Order Number"
          sortable
          :style="{ width: '20%', minWidth: '150px' }"
        />
        <Column
          field="supplier"
          header="Supplier"
          sortable
          :style="{ width: '30%', minWidth: '250px' }"
        />
        <Column
          field="total"
          header="Total"
          sortable
          :style="{ width: '20%', minWidth: '100px' }"
        />
      </DataTable>
    </div>
  </div>
</template>

<script setup>
//Primevue Imports

import { ref, onMounted } from 'vue';
import { createPurchaseOrders } from '@/data/faker'

const orders = ref()
const returnValue = ref('');
const viewType = ref('order')

// Populate data when the component is mounted
onMounted(() => {
    orders.value = createPurchaseOrders(50); // Generate 50 orders
});
</script>

<style scoped>
/* Style for active buttons */
:deep(.p-button.bg-symphony-yellow-500) {
    background-color: var(--symphony-yellow-500);
    border-color: var(--symphony-yellow-500);
    color: black;
}

:deep(.p-button.bg-symphony-yellow-500:hover) {
    background-color: var(--symphony-yellow-600);
    border-color: var(--symphony-yellow-600);
}

/* Fix for ButtonGroup in mobile view */
@media (max-width: 768px) {
    :deep(.p-buttongroup) {
        width: 100%;
    }
}

/* Custom styles for DataTable */
:deep(.p-datatable) {
    @apply h-full flex flex-col;
}

:deep(.p-datatable-wrapper) {
    @apply flex-1 overflow-auto;
}

:deep(.p-datatable-table) {
    @apply min-w-full;
}

/* Header Styling */
:deep(.p-datatable-thead > tr > th) {
    @apply bg-slate-50 p-4 font-semibold text-slate-700 text-sm uppercase tracking-wider border-b-2 border-slate-200;
}

/* Hover effect on header */
:deep(.p-datatable-thead > tr > th:hover) {
    @apply bg-slate-100;
}

/* Sort icons */
:deep(.p-sortable-column-icon) {
    @apply w-3 h-3 ml-2;
}

/* Active sort column */
:deep(.p-sortable-column.p-highlight) {
    @apply bg-slate-100 text-blue-600;
}

/* Add cursor pointer and hover effect for rows */
:deep(.p-datatable-tbody > tr:hover) {
    @apply bg-slate-50 cursor-pointer;
}

/* Make rows visually distinct */
:deep(.p-datatable-tbody > tr) {
    @apply transition-colors duration-200;
}
</style>